/**
 * Panel Admin: ofertas de asesoramiento experto (Academia > Asesores).
 */

import { useEffect, useMemo, useState } from 'react';
import { Handshake, Loader2, Plus, RefreshCw, Save, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  type AcademyExpertAdvisoryAdminRow,
  type AdvisoryStatus,
  useAdminAcademyExpertAdvisories,
  useSetAcademyExpertAdvisoryStatus,
  useUpsertAcademyExpertAdvisory,
} from '../hooks/useAcademyExpertAdvisoriesAdmin';

const STATUS_LABEL: Record<string, string> = {
  published: 'publicado',
  draft: 'borrador',
  archived: 'archivado',
};

const STATUS_STYLE: Record<string, string> = {
  published: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
  draft: 'bg-muted text-muted-foreground',
  archived: 'bg-amber-500/15 text-amber-800 dark:text-amber-300',
};

function topicsToText(raw: unknown): string {
  if (!raw) return '';
  if (Array.isArray(raw)) return raw.map((x) => String(x)).join('\n');
  return '';
}

function textToTopics(raw: string): string[] {
  return raw
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
}

function techniquesToText(arr: string[] | null | undefined): string {
  return (arr ?? []).join(', ');
}

function textToTechniques(raw: string): string[] {
  return raw
    .split(/[,\n]/)
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
}

function emptyForm() {
  return {
    slug: '',
    modality: '',
    title: '',
    tagline: '',
    description: '',
    collaboratorUserId: '',
    collaboratorDisplayName: '',
    coverImageUrl: '',
    whatsappPhone: '',
    priceEur: '30',
    durationMinutes: '45',
    topics: '',
    modalityTechniques: '',
    youtubeUrl: '',
    tiktokUrl: '',
    instagramUrl: '',
    facebookUrl: '',
    sortOrder: '0',
    status: 'draft' as AdvisoryStatus,
  };
}

type FormState = ReturnType<typeof emptyForm>;

function CoverThumb({ src, modality }: { src: string | null; modality: string }) {
  if (!src) {
    return (
      <div className="h-12 w-12 rounded-lg border border-dashed border-border bg-muted/40 flex items-center justify-center text-muted-foreground">
        <Handshake size={14} />
      </div>
    );
  }
  return (
    <div className="h-12 w-12 rounded-lg border border-border overflow-hidden bg-card">
      <img src={src} alt={modality} className="h-full w-full object-cover" />
    </div>
  );
}

export function AcademyExpertAdvisoriesPanel() {
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [msg, setMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  const { data, isLoading, isError, error, refetch, isFetching } = useAdminAcademyExpertAdvisories(
    statusFilter,
    search,
  );
  const upsert = useUpsertAcademyExpertAdvisory();
  const setStatus = useSetAcademyExpertAdvisoryStatus();

  const rows = data ?? [];
  const selected = useMemo(
    () => rows.find((r) => r.id === selectedId) ?? null,
    [rows, selectedId],
  );

  useEffect(() => {
    if (creating) return;
    if (!selected) {
      setForm(emptyForm());
      return;
    }
    setForm({
      slug: selected.slug,
      modality: selected.modality,
      title: selected.title,
      tagline: selected.tagline ?? '',
      description: selected.description ?? '',
      collaboratorUserId: selected.collaborator_user_id ?? '',
      collaboratorDisplayName: selected.collaborator_display_name,
      coverImageUrl: selected.cover_image_url,
      whatsappPhone: selected.whatsapp_phone,
      priceEur: String(selected.price_eur ?? 30),
      durationMinutes: String(selected.duration_minutes ?? 45),
      topics: topicsToText(selected.topics),
      modalityTechniques: techniquesToText(selected.modality_techniques),
      youtubeUrl: selected.youtube_url ?? '',
      tiktokUrl: selected.tiktok_url ?? '',
      instagramUrl: selected.instagram_url ?? '',
      facebookUrl: selected.facebook_url ?? '',
      sortOrder: String(selected.sort_order ?? 0),
      status: (selected.status as AdvisoryStatus) || 'draft',
    });
    setMsg(null);
  }, [selected, creating]);

  const startCreate = () => {
    setCreating(true);
    setSelectedId(null);
    setForm(emptyForm());
    setMsg(null);
  };

  const cancelEdit = () => {
    setCreating(false);
    setSelectedId(null);
    setForm(emptyForm());
    setMsg(null);
  };

  const onSave = async () => {
    setMsg(null);
    try {
      await upsert.mutateAsync({
        id: creating ? null : selectedId,
        slug: form.slug.trim(),
        modality: form.modality.trim(),
        title: form.title.trim(),
        tagline: form.tagline.trim(),
        description: form.description.trim(),
        collaboratorUserId: form.collaboratorUserId.trim() || null,
        collaboratorDisplayName: form.collaboratorDisplayName.trim(),
        coverImageUrl: form.coverImageUrl.trim(),
        whatsappPhone: form.whatsappPhone.trim(),
        priceEur: Number(form.priceEur) || 30,
        durationMinutes: Number(form.durationMinutes) || 45,
        topics: textToTopics(form.topics),
        modalityTechniques: textToTechniques(form.modalityTechniques),
        youtubeUrl: form.youtubeUrl.trim(),
        tiktokUrl: form.tiktokUrl.trim(),
        instagramUrl: form.instagramUrl.trim(),
        facebookUrl: form.facebookUrl.trim(),
        sortOrder: Number(form.sortOrder) || 0,
        status: form.status,
      });
      setMsg({ type: 'ok', text: creating ? 'Asesoramiento creado' : 'Cambios guardados' });
      setCreating(false);
      await refetch();
    } catch (e) {
      setMsg({
        type: 'err',
        text: e instanceof Error ? e.message : 'Error al guardar',
      });
    }
  };

  const patchStatus = async (row: AcademyExpertAdvisoryAdminRow, status: AdvisoryStatus) => {
    try {
      await setStatus.mutateAsync({ id: row.id, status });
      setMsg({ type: 'ok', text: `Estado → ${STATUS_LABEL[status] ?? status}` });
    } catch (e) {
      setMsg({
        type: 'err',
        text: e instanceof Error ? e.message : 'Error al cambiar estado',
      });
    }
  };

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const showForm = creating || Boolean(selected);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[180px] max-w-sm">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar modalidad, slug, colaborador…"
            className="pl-8 h-9"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="h-9 rounded-md border border-input bg-background px-2 text-sm"
        >
          <option value="all">Todos</option>
          <option value="published">Publicados</option>
          <option value="draft">Borrador</option>
          <option value="archived">Archivados</option>
        </select>
        <Button type="button" variant="outline" size="sm" onClick={() => void refetch()}>
          <RefreshCw className={cn('h-3.5 w-3.5 mr-1', isFetching && 'animate-spin')} />
          Actualizar
        </Button>
        <Button type="button" size="sm" onClick={startCreate}>
          <Plus className="h-3.5 w-3.5 mr-1" />
          Nuevo
        </Button>
      </div>

      {msg && (
        <p
          className={cn(
            'text-sm rounded-md px-3 py-2',
            msg.type === 'ok' ? 'bg-emerald-500/10 text-emerald-700' : 'bg-destructive/10 text-destructive',
          )}
        >
          {msg.text}
        </p>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-lg border border-border overflow-hidden">
          <div className="border-b border-border bg-muted/30 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Ofertas ({rows.length})
          </div>
          {isLoading ? (
            <div className="flex items-center justify-center gap-2 p-8 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Cargando…
            </div>
          ) : isError ? (
            <p className="p-4 text-sm text-destructive">
              {error instanceof Error ? error.message : 'Error'}
            </p>
          ) : rows.length === 0 ? (
            <p className="p-4 text-sm text-muted-foreground">No hay asesoramientos.</p>
          ) : (
            <ul className="divide-y divide-border max-h-[28rem] overflow-y-auto">
              {rows.map((row) => (
                <li key={row.id}>
                  <button
                    type="button"
                    onClick={() => {
                      setCreating(false);
                      setSelectedId(row.id);
                    }}
                    className={cn(
                      'flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-muted/40',
                      selectedId === row.id && !creating && 'bg-primary/10',
                    )}
                  >
                    <CoverThumb src={row.cover_image_url} modality={row.modality} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-bold">{row.modality}</p>
                      <p className="truncate text-[11px] text-muted-foreground">
                        {row.collaborator_display_name} · {row.slug}
                      </p>
                    </div>
                    <span
                      className={cn(
                        'shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold',
                        STATUS_STYLE[row.status] ?? STATUS_STYLE.draft,
                      )}
                    >
                      {STATUS_LABEL[row.status] ?? row.status}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="rounded-lg border border-border p-4">
          {!showForm ? (
            <p className="text-sm text-muted-foreground">
              Selecciona una oferta o crea una nueva. Aparecen como carrusel cuadrado encima de los
              filtros de Cursos en la app.
            </p>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-sm font-extrabold">
                  {creating ? 'Nuevo asesoramiento' : 'Editar asesoramiento'}
                </h3>
                <Button type="button" variant="ghost" size="sm" onClick={cancelEdit}>
                  Cancelar
                </Button>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label>Slug</Label>
                  <Input value={form.slug} onChange={(e) => setField('slug', e.target.value)} />
                </div>
                <div>
                  <Label>Modalidad (protagonista)</Label>
                  <Input
                    value={form.modality}
                    onChange={(e) => setField('modality', e.target.value)}
                    placeholder="Eging"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label>Título</Label>
                  <Input
                    value={form.title}
                    onChange={(e) => setField('title', e.target.value)}
                    placeholder="Asesoramiento experto en Eging"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label>Tagline</Label>
                  <Input
                    value={form.tagline}
                    onChange={(e) => setField('tagline', e.target.value)}
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label>Descripción</Label>
                  <textarea
                    value={form.description}
                    onChange={(e) => setField('description', e.target.value)}
                    rows={3}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <Label>Nombre colaborador</Label>
                  <Input
                    value={form.collaboratorDisplayName}
                    onChange={(e) => setField('collaboratorDisplayName', e.target.value)}
                  />
                </div>
                <div>
                  <Label>User ID (profiles)</Label>
                  <Input
                    value={form.collaboratorUserId}
                    onChange={(e) => setField('collaboratorUserId', e.target.value)}
                    placeholder="uuid…"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label>Cover URL (cuadrada)</Label>
                  <Input
                    value={form.coverImageUrl}
                    onChange={(e) => setField('coverImageUrl', e.target.value)}
                    placeholder="/images/academy/advisory/…"
                  />
                </div>
                <div>
                  <Label>WhatsApp (dígitos)</Label>
                  <Input
                    value={form.whatsappPhone}
                    onChange={(e) => setField('whatsappPhone', e.target.value)}
                    placeholder="34633446510"
                  />
                </div>
                <div>
                  <Label>Precio € / minutos</Label>
                  <div className="flex gap-2">
                    <Input
                      value={form.priceEur}
                      onChange={(e) => setField('priceEur', e.target.value)}
                    />
                    <Input
                      value={form.durationMinutes}
                      onChange={(e) => setField('durationMinutes', e.target.value)}
                    />
                  </div>
                </div>
                <div>
                  <Label>Orden</Label>
                  <Input
                    value={form.sortOrder}
                    onChange={(e) => setField('sortOrder', e.target.value)}
                  />
                </div>
                <div>
                  <Label>Estado</Label>
                  <select
                    value={form.status}
                    onChange={(e) => setField('status', e.target.value as AdvisoryStatus)}
                    className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                  >
                    <option value="draft">Borrador</option>
                    <option value="published">Publicado</option>
                    <option value="archived">Archivado</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <Label>Técnicas captura (coma, p.ej. eging)</Label>
                  <Input
                    value={form.modalityTechniques}
                    onChange={(e) => setField('modalityTechniques', e.target.value)}
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label>Temas (uno por línea)</Label>
                  <textarea
                    value={form.topics}
                    onChange={(e) => setField('topics', e.target.value)}
                    rows={4}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <Label>YouTube</Label>
                  <Input
                    value={form.youtubeUrl}
                    onChange={(e) => setField('youtubeUrl', e.target.value)}
                  />
                </div>
                <div>
                  <Label>Instagram</Label>
                  <Input
                    value={form.instagramUrl}
                    onChange={(e) => setField('instagramUrl', e.target.value)}
                  />
                </div>
                <div>
                  <Label>TikTok</Label>
                  <Input
                    value={form.tiktokUrl}
                    onChange={(e) => setField('tiktokUrl', e.target.value)}
                  />
                </div>
                <div>
                  <Label>Facebook</Label>
                  <Input
                    value={form.facebookUrl}
                    onChange={(e) => setField('facebookUrl', e.target.value)}
                  />
                </div>
              </div>

              <div className="flex flex-wrap gap-2 pt-1">
                <Button type="button" onClick={() => void onSave()} disabled={upsert.isPending}>
                  {upsert.isPending ? (
                    <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                  ) : (
                    <Save className="h-3.5 w-3.5 mr-1" />
                  )}
                  Guardar
                </Button>
                {!creating && selected && (
                  <>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => void patchStatus(selected, 'published')}
                    >
                      Publicar
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => void patchStatus(selected, 'archived')}
                    >
                      Archivar
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
